import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../lib/api';
import { Phone, Star, CalendarClock, UserPlus, RefreshCw, PhoneIncoming, Check, PhoneMissed } from 'lucide-react';
import { NewReservationDialog, ReservationDialog } from '../components/ReservationDialogs';

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fmtTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('de-DE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

function fmtUpcoming(u) {
  if (!u) return '';
  let d = u.date;
  try {
    d = new Date(u.date + 'T00:00:00').toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: 'short' });
  } catch { /* keep */ }
  return `${d} · ${u.start_time} · ${u.persons} Pers.`;
}

export default function AdminCalls() {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tables, setTables] = useState([]);
  const [areas, setAreas] = useState([]);
  const [createFor, setCreateFor] = useState(null);   // {phone, name}
  const [openResv, setOpenResv] = useState(null);
  const metaLoadedRef = useRef(false);

  const ensureMeta = useCallback(async () => {
    if (metaLoadedRef.current) return;
    try {
      const [a, t] = await Promise.all([api.get('/areas'), api.get('/tables')]);
      setAreas(a.data || []);
      setTables(t.data || []);
      metaLoadedRef.current = true;
    } catch { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/call-monitor/log', { params: { limit: 200 } });
      setCalls(r.data?.calls || []);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    ensureMeta();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load, ensureMeta]);

  const openUpcoming = async (callItem) => {
    if (!callItem.upcoming?.id) return;
    await ensureMeta();
    try {
      const r = await api.get(`/reservations/${callItem.upcoming.id}`);
      setOpenResv(r.data);
    } catch { /* ignore */ }
  };

  const markHandled = async (id) => {
    try {
      await api.post(`/call-monitor/${id}/handled`);
      load();
    } catch { /* ignore */ }
  };

  const missed = calls.filter(c => c.needs_callback);

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto" data-testid="admin-calls">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-wider text-[#5C5C59]">Anruferkennung</p>
          <h1 className="font-display text-3xl text-[#1C1C19] flex items-center gap-2">
            <PhoneIncoming className="w-7 h-7" /> Anrufverlauf
          </h1>
          <p className="text-sm text-[#5C5C59] mt-1">Eingehende Anrufe mit Gast-Erkennung — auch zum Zurückrufen.</p>
        </div>
        <button
          onClick={load}
          data-testid="calls-refresh"
          className="inline-flex items-center gap-2 border border-[#E5E5E0] px-4 py-2.5 rounded text-sm hover:bg-[#F9F9F7] transition"
        >
          <RefreshCw className="w-4 h-4" /> Aktualisieren
        </button>
      </div>

      {/* Callback list: incoming calls that still need a callback */}
      {missed.length > 0 && (
        <div className="bg-[#FEF2F2] border border-[#FCA5A5] rounded-lg p-4 mb-6" data-testid="callback-list">
          <div className="flex items-center gap-2 mb-3">
            <PhoneMissed className="w-5 h-5 text-[#DC2626]" />
            <h2 className="font-display text-lg text-[#1C1C19]">Offene Rückrufe: {missed.length}</h2>
          </div>
          <div className="space-y-2">
            {missed.map((c) => (
              <div key={c.id} className="bg-white border border-[#FECACA] rounded-md px-3 py-2.5 flex items-center gap-3 flex-wrap" data-testid={`callback-row-${c.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm text-[#1C1C19]">{c.phone}</span>
                    {c.name && (
                      <span className="text-sm font-medium text-[#1C1C19] inline-flex items-center gap-1">
                        {c.name}{c.is_regular && <Star className="w-3 h-3 text-[#B45309] fill-current" />}
                      </span>
                    )}
                    {!c.name && <span className="text-xs text-[#9CA3AF]">Neuer Gast</span>}
                  </div>
                  <span className="text-xs text-[#9CA3AF]">{fmtTime(c.received_at)}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <a
                    href={`tel:${(c.phone || '').replace(/\s/g, '')}`}
                    data-testid={`callback-dial-${c.id}`}
                    className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded bg-[#22C55E] text-white hover:bg-[#16A34A] transition"
                    title="Zurückrufen"
                  >
                    <Phone className="w-3.5 h-3.5" /> Zurückrufen
                  </a>
                  <button
                    onClick={() => { ensureMeta(); setCreateFor({ phone: c.phone, name: c.name || '' }); }}
                    data-testid={`callback-reserve-${c.id}`}
                    className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded border border-[#E5E5E0] hover:bg-[#F9F9F7] transition"
                  >
                    <UserPlus className="w-3.5 h-3.5" /> Reservieren
                  </button>
                  <button
                    onClick={() => markHandled(c.id)}
                    data-testid={`callback-done-${c.id}`}
                    className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded border border-[#E5E5E0] hover:bg-[#F9F9F7] transition"
                    title="Als erledigt markieren"
                  >
                    <Check className="w-3.5 h-3.5" /> Erledigt
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-[#E5E5E0] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="calls-table">
            <thead>
              <tr className="bg-[#F9F9F7] text-left text-xs uppercase tracking-wider text-[#5C5C59]">
                <th className="px-4 py-3 font-medium">Zeit</th>
                <th className="px-4 py-3 font-medium">Nummer</th>
                <th className="px-4 py-3 font-medium">Gast</th>
                <th className="px-4 py-3 font-medium hidden md:table-cell">Kommende Reservierung</th>
                <th className="px-4 py-3 font-medium text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-[#9CA3AF]">Lädt…</td></tr>
              ) : calls.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-[#9CA3AF]" data-testid="calls-empty">Noch keine Anrufe aufgezeichnet</td></tr>
              ) : (
                calls.map((c) => (
                  <tr key={c.id} className="border-t border-[#F0F0EC] hover:bg-[#F9F9F7]" data-testid="call-row">
                    <td className="px-4 py-3 text-[#5C5C59] whitespace-nowrap">{fmtTime(c.received_at)}</td>
                    <td className="px-4 py-3 font-mono text-xs text-[#1C1C19] whitespace-nowrap">{c.phone}</td>
                    <td className="px-4 py-3">
                      {c.name ? (
                        <span className="font-medium text-[#1C1C19] inline-flex items-center gap-1.5">
                          {c.name}
                          {c.is_regular && <Star className="w-3.5 h-3.5 text-[#B45309] fill-current" title="Stammgast" />}
                        </span>
                      ) : <span className="text-[#9CA3AF]">Neuer Gast</span>}
                      {c.reservation_created && (
                        <span className="ml-2 inline-flex items-center gap-1 text-[11px] text-[#166534] bg-[#DCFCE7] border border-[#86EFAC] rounded-full px-1.5 py-0.5"><Check className="w-3 h-3" />gebucht</span>
                      )}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      {c.upcoming ? (
                        <span className="inline-flex items-center gap-1 text-xs text-[#166534] bg-[#F0FDF4] border border-[#86EFAC] rounded-full px-2 py-0.5">
                          <CalendarClock className="w-3 h-3" />{fmtUpcoming(c.upcoming)}
                        </span>
                      ) : <span className="text-[#D1D5DB]">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <a
                          href={`tel:${(c.phone || '').replace(/\s/g, '')}`}
                          data-testid={`call-dial-${c.id}`}
                          className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded border border-[#E5E5E0] hover:bg-[#F9F9F7] transition"
                          title="Anrufen"
                        >
                          <Phone className="w-3.5 h-3.5" /> Anrufen
                        </a>
                        {c.upcoming ? (
                          <button
                            onClick={() => openUpcoming(c)}
                            data-testid={`call-open-${c.id}`}
                            className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded bg-[#1C1C19] text-white hover:bg-[#3A3A35] transition"
                          >
                            <CalendarClock className="w-3.5 h-3.5" /> Öffnen
                          </button>
                        ) : (
                          <button
                            onClick={() => { ensureMeta(); setCreateFor({ phone: c.phone, name: c.name || '' }); }}
                            data-testid={`call-create-${c.id}`}
                            className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded bg-[#22C55E] text-white hover:bg-[#16A34A] transition"
                          >
                            <UserPlus className="w-3.5 h-3.5" /> Reservieren
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <NewReservationDialog
        open={!!createFor}
        onClose={() => setCreateFor(null)}
        tables={tables}
        areas={areas}
        defaultDate={todayStr()}
        defaultPhone={createFor?.phone || ''}
        defaultName={createFor?.name || ''}
        onCreated={() => { setCreateFor(null); load(); }}
      />

      <ReservationDialog
        open={!!openResv}
        onClose={() => setOpenResv(null)}
        reservation={openResv}
        tables={tables}
        areas={areas}
        onUpdate={async () => {
          load();
          if (openResv?.id) {
            try {
              const r = await api.get(`/reservations/${openResv.id}`);
              setOpenResv(r.data);
            } catch { /* ignore */ }
          }
        }}
      />
    </div>
  );
}
